'use client'; // Error boundaries must be Client Components

import { SocialIcon } from '@/app_components/common/Footer';
import { Logo } from '@/app_components/common/Logo';
import { InternalError } from '@/app_components/errors/InternalError';
import { DescriptionParser } from '@/app_lib/htmlParser/ContentParser';
import { fortuneSocialLinks } from '@/appconstants';
import '@/app_styles/globals.css';
import { Suspense } from 'react';
import { registerAppRouterFonts } from 'styles/fonts';
import { FontRegistry, FontRegistryStyles } from 'utils/fontRegistry';
import { LoggerTags } from 'utils/log/constants/loggerTags';
import logToBackend from 'utils/log/logToBackend';

/*
 * Global Error has its own code tree in the app router, so it doesn't use the root layout file
 * Also cannot include the Global Scripts component as it is a server component
 * and this is a client component
 */
export default function GlobalError({ error }: { error: Error & { digest?: string } }) {
  registerAppRouterFonts();
  FontRegistry.seal();

  // Since React Server Components will only yield the error when caught by its boundary, we find ourselves
  // in a roundabout situation in which an error that has occurred on the server has to be reported back from
  // the client; This is by design in Next's App router, as even though it does log the errors on the server (in its own format),
  // we get no way to hook into them.
  // However, since the errors reach this component, we can send them back to the server through an API call
  logToBackend(LoggerTags.SERVER_ERROR, error.name, {
    message: error.message,
    traceback: error.stack,
    url: window.location.href,
  });

  const disclaimer =
    '<p>© 2025 Fortune Media IP Limited. All Rights Reserved. Use of this site constitutes acceptance of our <a href=\"https://fortune.com/terms-of-use/\">Terms of Use</a> and <a href=\"https://fortune.com/privacy-policy/\">Privacy Policy</a> | <a href=\"/california-privacy-policy/#notice\">CA Notice at Collection and Privacy Notice</a> | <a href=\"javascript:Optanon.ToggleInfoDisplay();\" target=\"_self\">Do Not Sell/Share My Personal Information</a><br />\nFORTUNE is a trademark of Fortune Media IP Limited, registered in the U.S. and other countries. FORTUNE may receive compensation for some links to products and services on this website. Offers may be subject to change without notice.</p>\n';

  return (
    <html lang='en'>
      <head>
        {/* We don't use <FontRegistryPreloadHeaders /> here as this is a Client Component */}
        <FontRegistryStyles />
      </head>
      <body className='antialiased bg-primary text-primary font-graphik-compact typography-default font-normal tracking-none'>
        <div className='min-h-screen flex flex-col justify-between'>
          {/* Fake Header */}
          <header>
            <div className='shadow-[0_15px_30px_0_rgba(0,0,0,0.1)] h-17 w-full flex flex-col items-center justify-center'>
              <Logo />
            </div>
          </header>
          {/* Main Error stuff */}
          <main>
            <InternalError />
          </main>
          <footer className='border-t-1 stroke-inverse px-8 pt-4 w-full'>
            <div className='flex flex-col xl:flex-row-reverse gap-4 pb-4 w-full items-center justify-between'>
              <ul className='flex items-center justify-center gap-2'>
                {fortuneSocialLinks.map(({ href, id }: { href: string; id: string }) => (
                  <li key={href}>
                    <SocialIcon
                      href={href}
                      icon={id}
                    />
                  </li>
                ))}
              </ul>
              <div className='typography-xs text-helper'>
                <Suspense fallback={null}>
                  <DescriptionParser content={disclaimer} />
                </Suspense>
              </div>
            </div>
          </footer>
        </div>
      </body>
    </html>
  );
}
